import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Actor,
  Command,
  Heat,
  Match,
  Tournament,
} from "@spikeeasy/tournament";
import { applyCommand } from "@spikeeasy/tournament";
import {
  actorId,
  api,
  hasRole,
  type Account,
  type PendingCommand,
  type Snapshot,
} from "./api";
import {
  cacheTournament,
  cachedTournament,
  discard,
  enqueue,
  flush,
  optimisticPoints,
  pending,
  rememberActor,
  rememberedActor,
  resumeAfterLogin,
} from "./offline";
import { Link } from "./Link";
import { Dialog, Field, modeLabels, Notice, teamLabel } from "./ui";
import { NowView, StandingsView, TeamsView } from "./TournamentViews";
import { DrawView } from "./DrawView";
import { HeatDialog, ScoreDialog } from "./Scoring";
import { ManageMatch, Organizer } from "./Organizer";
import { PointsEditor, Setup } from "./Setup";
import { Players } from "./Players";

interface Listing {
  id: string;
  name: string;
  status: string;
  mode: keyof typeof modeLabels;
  location: string;
  teamCount: number;
  date?: string;
}
const tournamentId = (path: string) =>
  path.match(/^\/t\/([a-zA-Z0-9_-]+)/)?.[1] ?? "";
type View = "now" | "draw" | "standings" | "teams" | "organize";

export function TournamentApp({
  path,
  go,
  account,
}: {
  path: string;
  go: (path: string) => void;
  account: Account | null;
}) {
  const id = tournamentId(path);
  const isAdmin = hasRole(account, "admin");
  const canViewPlayers = isAdmin || hasRole(account, "coach");
  const [list, setList] = useState<Listing[]>([]);
  const [actor, setActor] = useState<Actor | null>(null);
  const [actorKey, setActorKey] = useState<string | null>(null);
  const queueKey = actorId(actor, actorKey);
  const [t, setT] = useState<Tournament | null>(null);
  const [view, setView] = useState<View>("now");
  const [screen, setScreen] = useState<
    "main" | "new" | "edit" | "players" | "points"
  >("main");
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState("");
  const [queue, setQueue] = useState<PendingCommand[]>([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const updating = useRef<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [scoreId, setScoreId] = useState("");
  const [manageId, setManageId] = useState("");
  const [heatId, setHeatId] = useState("");
  const [serverTime, setServerTime] = useState(new Date().toISOString());
  const clockSample = useRef({
    server: Date.now(),
    received: performance.now(),
  });
  useEffect(() => {
    const interval = setInterval(() => {
      setServerTime(
        new Date(
          clockSample.current.server +
            performance.now() -
            clockSample.current.received,
        ).toISOString(),
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const tRef = useRef(t);
  const idRef = useRef(id);
  tRef.current = t;
  idRef.current = id;
  const navigate = (next: string) => go(next ? `/t/${next}` : "/");
  const created = useRef<Tournament | null>(null);
  const actorFromServer = useRef("");
  useEffect(() => {
    let current = true;
    const fresh = created.current?.id === id ? created.current : null;
    created.current = null;
    setScreen("main");
    setView(fresh ? "organize" : "now");
    setT(fresh);
    setLoading(!fresh);
    setActor(null);
    setActorKey(null);
    window.scrollTo(0, 0);
    if (id)
      void rememberedActor(id)
        .then((remembered) => {
          // Offline fallback only: the server's answer for this tournament always wins.
          if (current && remembered && actorFromServer.current !== id) {
            setActor(remembered.actor);
            setActorKey(remembered.key);
          }
        })
        .catch(() => {});
    return () => {
      current = false;
    };
  }, [id]);
  const acceptSnapshot = useCallback((snapshot: Snapshot) => {
    if (idRef.current !== snapshot.tournament.id) return;
    if (snapshot.actor !== undefined) {
      actorFromServer.current = snapshot.tournament.id;
      const remembered = {
        actor: snapshot.actor,
        key: snapshot.actorKey ?? null,
      };
      setActor(remembered.actor);
      setActorKey(remembered.key);
      void rememberActor(snapshot.tournament.id, remembered).catch(() => {});
      if (remembered.key) void resumeAfterLogin(remembered.key).catch(() => {});
    }
    setT((previous) =>
      previous?.id === snapshot.tournament.id &&
      previous.version > snapshot.tournament.version
        ? previous
        : snapshot.tournament,
    );
    setServerTime(snapshot.serverTime);
    clockSample.current = {
      server: Date.parse(snapshot.serverTime),
      received: performance.now(),
    };
    void cacheTournament(snapshot.tournament).catch(() =>
      setError(
        "Offline storage is unavailable. Keep this page online while scoring.",
      ),
    );
  }, []);
  const refresh = useCallback(async () => {
    try {
      if (id) acceptSnapshot(await api<Snapshot>(`/tournaments/${id}`));
      else
        setList(
          (await api<{ tournaments: Listing[] }>("/tournaments")).tournaments,
        );
      setOnline(true);
    } catch (e) {
      setOnline(false);
      if (!tRef.current && id) {
        const cached = await cachedTournament(id).catch(() => null);
        if (cached) setT(cached);
        else setError((e as Error).message);
      } else if (!id) setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [id, acceptSnapshot]);
  const update = useCallback(async () => {
    const key = `${id}:${queueKey}`;
    if (updating.current === key) return;
    updating.current = key;
    setRefreshing(true);
    try {
      await flush(queueKey, acceptSnapshot).catch(() => {});
      setQueue(await pending().catch(() => []));
      await refresh();
    } finally {
      if (updating.current === key) {
        updating.current = null;
        setRefreshing(false);
      }
    }
  }, [id, queueKey, refresh, acceptSnapshot]);
  useEffect(() => {
    const whenVisible = () => {
      if (document.visibilityState === "visible") void update();
    };
    const interval = setInterval(whenVisible, 30000);
    const offline = () => setOnline(false);
    window.addEventListener("online", whenVisible);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", whenVisible);
    whenVisible();
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", whenVisible);
      window.removeEventListener("offline", offline);
      document.removeEventListener("visibilitychange", whenVisible);
    };
  }, [update]);
  const mine = useMemo(
    () =>
      queue.filter(
        (item) => item.actor === queueKey && item.tournamentId === id,
      ),
    [queue, queueKey, id],
  );
  const visible = useMemo(
    () => (t ? optimisticPoints(t, mine, queueKey) : null),
    [t, mine, queueKey],
  );
  const send = async (command: Command) => {
    if (!t || !actor) throw new Error("Sign in to save scores.");
    setBusy(true);
    setError("");
    try {
      const requestId = crypto.randomUUID();
      if (
        ["point", "finish", "next_set", "finish_heat"].includes(command.type)
      ) {
        if (mine.some((item) => item.error))
          throw new Error(
            "Resolve the unsent updates before adding another score.",
          );
        if (visible && ["point", "next_set", "finish"].includes(command.type))
          applyCommand(visible, command, actor, serverTime);
        if (
          mine.some(
            (item) =>
              ("matchId" in command &&
                item.command.type === "finish" &&
                item.command.matchId === command.matchId) ||
              ("heatId" in command &&
                item.command.type === "finish_heat" &&
                item.command.heatId === command.heatId),
          )
        )
          throw new Error("This final result is already waiting to sync.");
        await enqueue({
          id: requestId,
          tournamentId: t.id,
          actor: queueKey,
          version: t.version,
          command,
          createdAt: Date.now(),
        });
        setQueue(await pending());
        if (navigator.onLine)
          void flush(queueKey, acceptSnapshot)
            .then(async () => setQueue(await pending()))
            .catch(() =>
              setError(
                "Your update is saved on this device. Retrying when connected.",
              ),
            );
      } else
        acceptSnapshot(
          await api<Snapshot>(`/tournaments/${t.id}/commands`, {
            id: requestId,
            version: t.version,
            command,
          }),
        );
    } finally {
      setBusy(false);
    }
  };
  const manage = (match: Match) => {
    if (match.resolution === "bye") return;
    if (match.status === "complete") setScoreId(match.id);
    else setManageId(match.id);
  };
  const activeScore = visible?.matches.find((m) => m.id === scoreId);
  const activeManage = visible?.matches.find((m) => m.id === manageId);
  const activeHeat = visible?.heats.find((h) => h.id === heatId);
  const startHeat = async (heat: Heat) => {
    try {
      await send({ type: "start_heat", heatId: heat.id });
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const onCreated = (tournament: Tournament) => {
    created.current = tournament;
    if (tournament.id === id) {
      setT(tournament);
      setScreen("main");
      setView("organize");
    } else navigate(tournament.id);
  };
  return (
    <>
      <main id="main" className="app-main">
        {screen === "main" && (
          <div className="refresh-controls">
            <small>Updates every 30 seconds</small>
            <button
              type="button"
              className="text-button"
              disabled={refreshing}
              aria-busy={refreshing}
              onClick={() => void update()}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        )}
        {!online && (
          <Notice>
            Offline or reconnecting. Scores saved on this device will sync when
            the connection returns. Unconfirmed results do not advance the draw.
          </Notice>
        )}
        {error && (
          <Notice error>
            <p>{error}</p>
            <button
              className="text-button"
              onClick={() => {
                setError("");
                void refresh();
              }}
            >
              Retry
            </button>
          </Notice>
        )}
        {mine.length > 0 && (
          <Notice error={mine.some((item) => !!item.error)}>
            <strong>
              {mine.length} unsent update{mine.length === 1 ? "" : "s"}
            </strong>
            {mine.find((item) => item.error)?.error ? (
              <>
                <p>{mine.find((item) => item.error)?.error}</p>
                {mine.some((item) => item.errorStatus === 401) && (
                  <button
                    className="primary"
                    onClick={() => setLoginOpen(true)}
                  >
                    Sign in again to send saved scores
                  </button>
                )}
                <button
                  className="text-button"
                  onClick={async () => {
                    if (
                      window.confirm(
                        "Discard all unsent updates for this tournament? Already accepted scores remain saved.",
                      )
                    ) {
                      for (const item of mine) await discard(item.id);
                      setQueue(await pending());
                      await refresh();
                    }
                  }}
                >
                  Discard unsent updates & review saved scores
                </button>
              </>
            ) : (
              <p>Keep this device until all updates are confirmed.</p>
            )}
          </Notice>
        )}
        {screen === "players" && canViewPlayers ? (
          <Players done={() => setScreen("main")} canManage={isAdmin} />
        ) : screen === "points" && isAdmin ? (
          <PointsEditor done={() => setScreen("main")} />
        ) : (screen === "new" || screen === "edit") && isAdmin ? (
          <Setup
            existing={screen === "edit" && t ? t : undefined}
            saved={onCreated}
            cancel={() => setScreen("main")}
          />
        ) : id ? (
          <>
            {loading && !visible ? (
              <Notice>Loading tournament…</Notice>
            ) : (
              visible && (
                <>
                  <div className="tournament-heading">
                    <p className="eyebrow">
                      {modeLabels[visible.config.mode]} · {visible.teams.length}{" "}
                      teams
                    </p>
                    <h1>{visible.name}</h1>
                    <p>{visible.location}</p>
                    {!actor && visible.status === "running" && (
                      <p className="scoring-access">
                        Playing today?{" "}
                        {account ? (
                          "Your account isn't on a team in this tournament. "
                        ) : (
                          <>
                            <Link
                              go={go}
                              href={`/sign-in?next=${encodeURIComponent(`/t/${visible.id}`)}`}
                            >
                              Sign in
                            </Link>{" "}
                            to score your matches, or{" "}
                          </>
                        )}
                        <button
                          type="button"
                          className="text-button"
                          onClick={() => setLoginOpen(true)}
                        >
                          {account ? "Use a team phrase" : "use your team phrase"}
                        </button>
                        .
                      </p>
                    )}
                  </div>
                  {visible.advancementIssue && (
                    <Notice>
                      Waiting for an organizer decision:{" "}
                      {visible.advancementIssue}
                    </Notice>
                  )}
                  {view === "now" && (
                    <NowView
                      t={visible}
                      actor={actor}
                      onScore={(m) => setScoreId(m.id)}
                      onManage={manage}
                      onHeat={(h) => setHeatId(h.id)}
                      startHeat={startHeat}
                      now={serverTime}
                    />
                  )}
                  {view === "draw" && (
                    <DrawView
                      t={visible}
                      actor={actor}
                      onScore={(m) => setScoreId(m.id)}
                      onManage={manage}
                      onHeat={(h) => setHeatId(h.id)}
                    />
                  )}
                  {view === "standings" && <StandingsView t={visible} />}
                  {view === "teams" && <TeamsView t={visible} actor={actor} />}
                  {view === "organize" && actor?.role === "admin" && (
                    <Organizer
                      t={visible}
                      send={send}
                      busy={busy}
                      edit={() => setScreen("edit")}
                    />
                  )}
                </>
              )
            )}
          </>
        ) : (
          <>
            <div className="tournament-heading">
              <p className="eyebrow">Spike Easy on the sand</p>
              <h1>Tournament days.</h1>
              <p>Find your court. Follow the draw. Keep the score.</p>
            </div>
            {canViewPlayers && (
              <div className="organizer-toolbar">
                {isAdmin && (
                  <button className="primary" onClick={() => setScreen("new")}>
                    New tournament
                  </button>
                )}
                <button
                  className="text-button"
                  onClick={() => setScreen("players")}
                >
                  Players & points
                </button>
                {isAdmin && (
                  <button
                    className="text-button"
                    onClick={() => setScreen("points")}
                  >
                    Points tables
                  </button>
                )}
              </div>
            )}
            {loading ? (
              <Notice>Loading tournaments…</Notice>
            ) : list.length === 0 ? (
              <section className="empty-state">
                <h2>The courts are quiet.</h2>
                <p>
                  Published tournaments appear here with their schedule, draw
                  and live scores.
                </p>
                {isAdmin && (
                  <p>
                    Start by adding players and creating a points table, then
                    build your first draw.
                  </p>
                )}
              </section>
            ) : (
              <div className="tournament-list">
                {list.map((item) => (
                  <a
                    key={item.id}
                    href={`/t/${item.id}`}
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                        navigate(item.id);
                      }
                    }}
                  >
                    <div>
                      <small>
                        {item.date} · {modeLabels[item.mode]}
                      </small>
                      <h2>{item.name}</h2>
                      <p>
                        {item.location} · {item.teamCount} teams
                      </p>
                    </div>
                    <span>
                      {item.status === "draft"
                        ? "Draft"
                        : item.status === "finished"
                          ? "Results"
                          : "View tournament"}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 12h15m-6-6 6 6-6 6" />
                      </svg>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      {id && visible && screen === "main" && (
        <nav className="app-nav" aria-label="Tournament views">
          {(
            [
              "now",
              "draw",
              "standings",
              "teams",
              ...(actor?.role === "admin" ? ["organize"] : []),
            ] as View[]
          ).map((item) => (
            <button
              key={item}
              aria-current={view === item ? "page" : undefined}
              onClick={() => {
                setView(item);
                window.scrollTo(0, 0);
              }}
            >
              {
                {
                  now: "Courts",
                  draw: "Draw",
                  standings: "Standings",
                  teams: "Teams",
                  organize: "Organize",
                }[item]
              }
            </button>
          ))}
        </nav>
      )}
      {loginOpen && t && (
        <Login
          t={t}
          go={go}
          close={() => setLoginOpen(false)}
          done={async () => {
            setLoginOpen(false);
            await refresh();
          }}
        />
      )}
      {visible && activeScore && actor && (
        <ScoreDialog
          key={activeScore.id}
          t={visible}
          match={activeScore}
          actor={actor}
          send={send}
          close={() => setScoreId("")}
          busy={busy}
          queued={mine.length > 0}
        />
      )}
      {visible && activeManage && actor?.role === "admin" && (
        <ManageMatch
          key={activeManage.id}
          t={visible}
          match={activeManage}
          send={send}
          close={() => setManageId("")}
          busy={busy}
        />
      )}
      {visible && activeHeat && actor && (
        <HeatDialog
          key={activeHeat.id}
          t={visible}
          heat={activeHeat}
          send={send}
          close={() => setHeatId("")}
          busy={busy}
          serverTime={serverTime}
        />
      )}
    </>
  );
}

function Login({
  t,
  go,
  close,
  done,
}: {
  t: Tournament;
  go: (path: string) => void;
  close: () => void;
  done: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [teamId, setTeam] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title="Score with a team phrase" onClose={close}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            await api("/login", {
              kind: "team",
              tournamentId: t.id,
              teamId,
              code,
            });
            await done();
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Your team">
          <select
            required
            value={teamId}
            onChange={(e) => setTeam(e.target.value)}
          >
            <option value="">Choose your team</option>
            {t.teams.map((team) => (
              <option key={team.id} value={team.id}>
                {teamLabel(team)}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Team phrase"
          hint="Four words from the organizer, for example otter-mango-tennis-plum."
        >
          <input
            type="password"
            required
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        {error && <Notice error>{error}</Notice>}
        <button className="primary" disabled={busy}>
          {busy ? "Signing in…" : "Score for this team"}
        </button>
        <p className="muted">
          You can score your own games and the games you referee. Have an
          account?{" "}
          <Link
            go={go}
            href={`/sign-in?next=${encodeURIComponent(`/t/${t.id}`)}`}
            onClick={close}
          >
            Sign in with your email
          </Link>
          .
        </p>
      </form>
    </Dialog>
  );
}
